import { BrowserCompatibility } from './BrowserCompatibility'
import { FallbackFileService } from './FallbackFileService'
import { demoTracker } from '$lib/stores/demoStore'
import { toast } from '$lib/stores/toast'

/**
 * Unified file service that automatically uses the appropriate method
 * based on browser capabilities
 */
export class UnifiedFileService {
  private static mode = BrowserCompatibility.getStorageMode()
  
  /**
   * Get current storage mode
   */
  static getMode() {
    return this.mode
  }

  /**
   * Check if using fallback mode
   */
  static isUsingFallback(): boolean {
    return this.mode === 'fallback'
  }

  /**
   * Open a file
   */
  static async openFile(): Promise<{
    handle: FileSystemFileHandle | any
    content: string
    name?: string
  } | null> {
    // Check if demo mode
    const isDemo = demoTracker.isDemoMode()

    // Demo mode check for iframe embedding with no file API support
    if (isDemo) {
      // Check if running in an iframe
      const isInIframe = window.self !== window.top

      // Check if File API is supported
      const hasFileAPI = 'showOpenFilePicker' in window ||
                        (window as any).api?.openFile ||
                        (typeof File !== 'undefined' && typeof FileReader !== 'undefined')

      if (isInIframe && !hasFileAPI) {
        toast.warning('데모 모드에서는 파일 열기가 제한됩니다. 새 문서를 만들어 테스트해보세요.', 5000)
        return null
      }
    }

    // Electron mode
    if (this.mode === 'electron' && window.api?.openFile) {
      const result = await window.api.openFile()
      if (result) {
        return {
          handle: {
            name: result.name,
            path: result.path,
            isElectron: true
          },
          content: result.content,
          name: result.name
        }
      }
      return null
    }

    // Native File System Access API
    if (this.mode === 'native' && window.showOpenFilePicker) {
      try {
        const [fileHandle] = await window.showOpenFilePicker({
          types: [
            {
              description: 'Markdown files',
              accept: {
                'text/markdown': ['.md'],
                'text/plain': ['.txt']
              },
            },
          ],
          multiple: false,
        })

        const file = await fileHandle.getFile()
        const content = await file.text()

        return { 
          handle: fileHandle, 
          content,
          name: fileHandle.name 
        }
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('Failed to open file:', err)
        }
        return null
      }
    }

    // Fallback mode
    try {
      const result = await FallbackFileService.openFile()
      if (result) {
        return {
          handle: result.handle || { name: result.name, isFallback: true },
          content: result.content,
          name: result.name
        }
      }
    } catch (err) {
      console.error('Failed to open file in fallback mode:', err)

      // Check if it's due to iframe restrictions
      if (isDemo && window.self !== window.top) {
        toast.error('데모 모드에서 파일 열기를 사용할 수 없습니다. 샘플 문서로 기능을 테스트해보세요.', 5000)
      } else {
        toast.error('파일을 열 수 없습니다. 브라우저 설정을 확인해주세요.', 4000)
      }
    }

    return null
  }

  /**
   * Open a directory (not supported in fallback mode)
   */
  static async openDirectory(): Promise<FileSystemDirectoryHandle | null> {
    if (this.mode === 'electron' && window.api?.openDirectory) {
      // Electron would return a custom handle
      const result = await window.api.openDirectory()
      return result as any
    }

    if (this.mode === 'native' && window.showDirectoryPicker) {
      try {
        return await window.showDirectoryPicker()
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('Failed to open directory:', err)
        }
        return null
      }
    }

    // Fallback mode - directories not supported
    console.warn('Directory access not supported in fallback mode')
    return null
  }

  /**
   * Save file
   */
  static async saveFile(
    handle: FileSystemFileHandle | any,
    content: string
  ): Promise<boolean> {
    // Electron mode
    if (handle?.isElectron && window.api?.saveFile) {
      return await window.api.saveFile(handle.path, content)
    }

    // Native mode with real handle
    if (this.mode === 'native' && handle && 'createWritable' in handle) {
      try {
        const writable = await handle.createWritable()
        await writable.write(content)
        await writable.close()
        return true
      } catch (err) {
        console.error('Failed to save file:', err)
        return false
      }
    }

    // Fallback mode or no valid handle
    const filename = handle?.name || 'untitled.md'
    return FallbackFileService.saveFile(filename, content)
  }

  /**
   * Save as new file
   */
  static async saveAsNewFile(
    content: string,
    suggestedName?: string
  ): Promise<FileSystemFileHandle | any | null> {
    // Electron mode
    if (this.mode === 'electron' && window.api?.saveAsNewFile) {
      const result = await window.api.saveAsNewFile(content, suggestedName)
      if (result) {
        return {
          name: result.name,
          path: result.path,
          isElectron: true
        }
      }
      return null
    }

    // Native mode
    if (this.mode === 'native' && window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: suggestedName || 'untitled.md',
          types: [
            {
              description: 'Markdown files',
              accept: {
                'text/markdown': ['.md'],
                'text/plain': ['.txt']
              },
            },
          ],
        })

        await this.saveFile(handle, content)
        return handle
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('Failed to save new file:', err)
        }
        return null
      }
    }

    // Fallback mode
    const filename = suggestedName || 'untitled.md'
    if (FallbackFileService.saveAsNewFile(content, filename)) {
      return {
        name: filename,
        isFallback: true
      }
    }
    return null
  }

  /**
   * Get browser compatibility info
   */
  static getBrowserInfo() {
    return BrowserCompatibility.getBrowserInfo()
  }

  /**
   * Show compatibility warning if needed
   */
  static showCompatibilityWarning(): boolean {
    if (this.mode === 'fallback') {
      const browserName = BrowserCompatibility.getBrowserName()
      const isIOS = BrowserCompatibility.isIOS
      
      let message = `${browserName}${isIOS ? ' on iOS' : ''}에서는 파일 시스템 API가 제한적입니다.\n\n`
      message += '사용 가능한 기능:\n'
      message += '• 파일 열기 (업로드)\n'
      message += '• 파일 저장 (다운로드)\n\n'
      message += '제한된 기능:\n'
      message += '• 폴더 열기 불가\n'
      message += '• 실시간 파일 감시 불가\n'
      message += '• 자동 저장 시 다운로드 창 표시\n\n'
      message += '전체 기능을 사용하려면 Chrome, Edge 또는 데스크톱 앱을 이용해주세요.'
      
      // Return true if warning should be shown
      return true
    }
    return false
  }

  /**
   * Get fallback file list (for session)
   */
  static getFallbackFiles() {
    if (this.mode === 'fallback') {
      return FallbackFileService.getFileList()
    }
    return []
  }
}